import {
  CurrencyWrapper,
  DataTable,
  DataTableColumnHeader,
  Text,
} from "@/components/ui";
import { formatDate } from "@/lib/utils/format";
import type { tanstackTableNames } from "@/lib/data/static/tanstack-table-names";
import type { LawWorksCharge } from "@/lib/types";
import { createColumnHelper, type ColumnDef } from "@tanstack/react-table";
import { Link } from "@tanstack/react-router";

const columnHelper = createColumnHelper<LawWorksCharge>();

export const getLawWorksChargeColumns = (showMatter = false) =>
  [
    columnHelper.accessor("dateCreated", {
      id: "Date",
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      cell: ({ getValue }) => (
        <Text variant="subHeading">{formatDate(getValue()) ?? "-"}</Text>
      ),
    }),
    columnHelper.accessor("description", {
      id: "Description",
      enableHiding: false,
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      cell: ({ getValue }) => (
        <Text variant="body" className="font-semibold">
          {getValue()}
        </Text>
      ),
    }),
    columnHelper.accessor("amount", {
      id: "Amount",
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      cell: ({ getValue }) => <CurrencyWrapper value={Number(getValue())} />,
    }),
    ...(showMatter
      ? [
          columnHelper.accessor("matterId", {
            id: "Matter",
            header: ({ column }) => <DataTableColumnHeader column={column} />,
            cell: ({ getValue }) => {
              const matterId = getValue();
              return matterId ? (
                <Link
                  to={`/matters/${matterId}`}
                  className="text-primary hover:underline"
                >
                  View matter
                </Link>
              ) : (
                "-"
              );
            },
          }),
        ]
      : []),
  ] as ColumnDef<LawWorksCharge>[];

type LawWorksChargesTableProps = {
  charges: LawWorksCharge[];
  isError: boolean;
  isFetching: boolean;
  isLoading: boolean;
  tableName:
    | (typeof tanstackTableNames)["firmLawWorksCharges"]
    | (typeof tanstackTableNames)["matterLawWorksCharges"];
  totalCount: number;
  pageIndex: number;
  pageSize: number;
  onPaginationChange: (pageIndex: number, pageSize: number) => void;
  showMatter?: boolean;
};

export function LawWorksChargesTable({
  charges,
  isError,
  isFetching,
  isLoading,
  tableName,
  totalCount,
  pageIndex,
  pageSize,
  onPaginationChange,
  showMatter = false,
}: LawWorksChargesTableProps) {
  const pageCount = Math.max(1, Math.ceil(totalCount / pageSize));

  return (
    <DataTable
      entityLabelPlural="charges"
      allowLocalStorage={false}
      columns={getLawWorksChargeColumns(showMatter)}
      data={charges}
      hideSearchBar
      isError={isError}
      isFetching={isFetching}
      isLoading={isLoading}
      noResultsMessage="No LawWorks charges found."
      onRowClick={() => undefined}
      tableName={tableName}
      serverPagination={{
        pagination: { pageIndex, pageSize },
        onPaginationChange: (updater) => {
          const next =
            typeof updater === "function"
              ? updater({ pageIndex, pageSize })
              : updater;
          onPaginationChange(next.pageIndex, next.pageSize);
        },
        pageCount,
        rowCount: totalCount,
        getCanNextPage: (pageIndex + 1) * pageSize < totalCount,
      }}
    />
  );
}
